/**
 * resør · Stock / Bespoke order switch
 * ---------------------------------------------------------------------------
 * Custom element <resor-order-switch>, rendered by snippets/resor-order-switch.liquid.
 *
 * Modes
 *   bespoke (default): the page's normal purchase flow (price, configurator,
 *                      Add to cart). The stock panel is hidden.
 *   stock:             the page's bespoke-only elements are hidden and the
 *                      stock panel (unit cards, price, own Add to cart) is shown.
 *
 * What it does
 *   1. Switches between the two modes and keeps the URL in sync
 *      (?build=stock&unit=<handle>) so a stock unit can be deep-linked.
 *   2. On entering stock mode, re-checks the units' availability with ONE
 *      request (/collections/<stock collection>/products.json) in case a unit
 *      sold out after the page was cached; /products/<handle>.js is the
 *      fallback for any unit not found there. Sold-out units are disabled
 *      (their card reads the sold-out text) and the "[count] in stock" line
 *      is lowered to match; if none are left, the page falls
 *      back to Bespoke and the In-stock option is disabled with the
 *      "none in stock" text.
 *   3. Adds the chosen stock unit to the cart via the AJAX Cart API, with a
 *      visible line-item property ("Build: In stock · …"), then asks the
 *      Motion theme to refresh the cart drawer.
 *   4. Adds the matching property ("Build: Bespoke · …") to the page's own
 *      product form so bespoke lines are labelled too.
 *   5. Dispatches `resor:mode-change` ({ mode }) on document whenever the mode
 *      changes (used by resor-family-strip.js).
 *   6. Keeps the Bespoke price in the price head (under the title) in step
 *      with the selected variant. Motion's price block is hidden on switch
 *      pages, so Motion's `variantPriceChange` event is followed instead.
 *      The In Stock price there is the selected unit's (selectUnit).
 *   7. Shows pickup availability for the selected unit in the stock panel
 *      ("Pickup available at our showroom" + "View store information").
 *      Motion renders it inside the Buy buttons block, which is hidden in
 *      stock mode and describes the bespoke product, so the same Shopify
 *      section is loaded here for the unit (see loadPickup).
 *
 * Loading and speed
 *   - Loaded async, after the <resor-order-switch> markup, so it runs as soon
 *     as it is downloaded instead of at the end of the theme's deferred queue.
 *   - The initial mode is already on the page before this file runs: the inline
 *     boot script in the snippet sets data-mode / data-resor-mode from the URL,
 *     and resor-order-switch.css renders both modes from those attributes.
 *     This file re-applies the same state (idempotent) and adds behaviour.
 *   - Because it may run while the rest of the product section is still being
 *     parsed, the JS-side hiding of bespoke-only elements is repeated once the
 *     DOM is complete (see connectedCallback).
 *
 * Motion theme integration (v12)
 *   - Bespoke-only elements are found with Motion's own markup
 *     (see BESPOKE_ONLY_SELECTORS), sales points matching the keywords in
 *     Theme settings → resør · Product page → Sales points to hide.
 *     To hide another bespoke-only element, add its selector to
 *     BESPOKE_ONLY_SELECTORS.
 *   - After adding a stock unit it follows Theme settings → Cart → Cart type:
 *     "Page" goes to the cart page; "Drawer" fires Motion's `ajaxProduct:added`
 *     event, which rebuilds and opens #CartDrawer (same as the native form).
 *   - The EasyFlow configurator (.ef__product-option-root) is treated as
 *     bespoke-only and hidden in stock mode. Its logic is not touched.
 *
 * No dependencies. Safe to load more than once.
 */
(() => {
  if (customElements.get('resor-order-switch')) return;

  /**
   * Motion elements that belong to the bespoke (native) purchase flow.
   * Each match is hidden together with its block wrapper while "In stock" is active.
   */
  const BESPOKE_ONLY_SELECTORS = [
    '.product-block--price', // native price
    '[data-dynamic-variants-enabled]', // variant picker block
    '.product__quantity', // native quantity selector
    '.ef__product-option-root', // EasyFlow Product Options configurator (bespoke options)
    '.downpay-block', // Downpay deposit widget (deposit applies to bespoke only)
  ];

  const HIDDEN_CLASS = 'resor-is-hidden';
  /** Minimum gap between two live stock re-checks (ms). Toggling back and forth doesn't re-fetch. */
  const AVAILABILITY_TTL = 30 * 1000;
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  /**
   * Pickup availability (Motion 12, theme.js → theme.StoreAvailability).
   * The theme's holder marks that the Buy buttons block has "Show pickup
   * availability" ticked; no holder = pickup is off, so stock mode shows none either.
   * The loaded section contains a "View store information" drawer with a fixed
   * id. The theme's own copy (bespoke) stays on the page, so ours is renamed
   * to keep ids unique, gets its own opener class and is moved out of the
   * switch (see loadPickup).
   */
  const PICKUP_HOLDER = '[data-store-availability-holder]';
  const PICKUP_DRAWER_ID = 'StoreAvailabilityDrawer';
  const PICKUP_OPENER_CLASS = 'js-drawer-open-availability';
  const RESOR_DRAWER_ID = 'ResorStoreAvailabilityDrawer';
  const RESOR_DRAWER_NAME = 'resor-availability'; // Motion's Drawers binds ".js-drawer-open-" + name

  class ResorOrderSwitch extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.section = this.closest('.product-section') || document;
      this.propertyKey = this.dataset.propertyKey || 'Build';

      this.modeInputs = [...this.querySelectorAll('[data-resor-mode-input]')];
      this.stockModeLabel = this.querySelector('[data-resor-stock-mode]');
      this.stockNote = this.querySelector('[data-resor-stock-note]');
      this.unitInputs = [...this.querySelectorAll('[data-resor-unit-input]')];
      this.stockForm = this.querySelector('[data-resor-stock-form]');
      this.variantIdInput = this.querySelector('[data-resor-variant-id]');
      this.qtyInput = this.querySelector('[data-resor-qty]');
      this.priceEl = this.querySelector('[data-resor-stock-price]');
      this.stockCountEl = this.querySelector('[data-resor-stock-count]');
      this.bespokePriceEl = this.querySelector('[data-resor-bespoke-price]');
      this.errorEl = this.querySelector('[data-resor-error]');
      this.submitBtn = this.querySelector('[data-resor-submit]');
      this.pickupEl = this.querySelector('[data-resor-pickup]');

      this.labelBespokeForm();

      // Motion dispatches variantPriceChange on the product section without
      // bubbling, so listen in the capture phase and keep only this section's.
      document.addEventListener(
        'variantPriceChange',
        (event) => {
          if (event.target instanceof Node && event.target.contains(this)) {
            this.updateBespokePrice(event.detail && event.detail.variant);
          }
        },
        true
      );

      this.modeInputs.forEach((input) =>
        input.addEventListener('change', () => this.setMode(input.value))
      );
      this.unitInputs.forEach((input) =>
        input.addEventListener('change', () => this.selectUnit(input))
      );
      if (this.stockForm) {
        this.stockForm.addEventListener('submit', (event) => this.onSubmit(event));
      }

      this.applyInitialState();

      // Async script: blocks after the switch (Add to cart form, sales points)
      // may not be parsed yet. Once the DOM is complete, label the native form
      // and re-apply the JS-side hiding. The CSS rules already cover Motion's
      // known blocks from first paint, so nothing visible waits for this.
      if (document.readyState === 'loading') {
        document.addEventListener(
          'DOMContentLoaded',
          () => {
            this.labelBespokeForm();
            this.applyMode();
            // The Buy buttons block (and its pickup holder) sits after the switch,
            // so on a ?build=stock link it may not have existed on the first try.
            if (this.mode === 'stock') this.loadPickup();
          },
          { once: true }
        );
      }
    }

    /* ------------------------------------------------------------------ */
    /* Mode                                                               */
    /* ------------------------------------------------------------------ */

    /** Reads ?build=stock&unit=<handle> and sets the starting mode. */
    applyInitialState() {
      const params = new URLSearchParams(window.location.search);
      const wantsStock = params.get('build') === 'stock' && this.hasAvailableUnits();

      if (wantsStock) {
        const handle = params.get('unit');
        const match = handle && this.unitInputs.find((i) => i.dataset.handle === handle && !i.disabled);
        if (match) {
          match.checked = true;
          this.selectUnit(match, { updateUrl: false });
        }
      }
      this.setMode(wantsStock ? 'stock' : 'bespoke', { updateUrl: false });
    }

    /**
     * @param {'bespoke'|'stock'} mode
     * @param {{updateUrl?: boolean}} [options]
     */
    setMode(mode, { updateUrl = true } = {}) {
      if (mode === 'stock' && !this.hasAvailableUnits()) mode = 'bespoke';
      const isStock = mode === 'stock';

      this.mode = mode;
      this.applyMode();
      this.clearError();

      if (isStock) {
        this.refreshAvailability();
        this.loadPickup();
      }
      if (updateUrl) this.syncUrl();

      // Lets other resør components (e.g. the family strip) follow the choice.
      document.dispatchEvent(new CustomEvent('resor:mode-change', { detail: { mode } }));
    }

    /**
     * Writes the current mode to the DOM. Idempotent; the boot script in the
     * snippet does the attribute part before this file loads, so calling it
     * again only adds the JS-only hiding (custom selectors, sales points).
     * The stock panel needs no toggling here: CSS shows it from data-mode.
     */
    applyMode() {
      const isStock = this.mode === 'stock';
      this.dataset.mode = this.mode;
      if (this.section.dataset) this.section.dataset.resorMode = this.mode;

      this.modeInputs.forEach((input) => {
        input.checked = input.value === this.mode;
        input.closest('.resor-switch__mode').classList.toggle('is-active', input.checked);
      });
      this.getBespokeOnlyElements().forEach((el) => el.classList.toggle(HIDDEN_CLASS, isStock));
    }

    /** Elements of the native purchase flow, resolved to their block wrapper. */
    getBespokeOnlyElements() {
      const found = new Set();

      BESPOKE_ONLY_SELECTORS.forEach((selector) => {
        this.section.querySelectorAll(selector).forEach((el) => {
          if (!this.contains(el)) found.add(this.toBlock(el));
        });
      });

      this.getNativeForms().forEach((form) => found.add(this.toBlock(form)));
      this.getBespokeSalesPoints().forEach((el) => found.add(this.toBlock(el)));
      return [...found];
    }

    /**
     * Motion "sales point" blocks whose text mentions a bespoke-only topic
     * (Theme settings → resør · Product page → Sales points to hide),
     * e.g. "Production time is around 7-8 months".
     */
    getBespokeSalesPoints() {
      const keywords = (this.dataset.bespokeSalesPoints || '')
        .split(',')
        .map((k) => k.trim().toLowerCase())
        .filter(Boolean);
      if (!keywords.length) return [];

      return [...this.section.querySelectorAll('.product-block--sales-point')].filter((el) => {
        const text = el.textContent.toLowerCase();
        return keywords.some((k) => text.includes(k));
      });
    }

    /** The theme's own add-to-cart form(s) in this section (not ours). */
    getNativeForms() {
      return [...this.section.querySelectorAll('form[action*="/cart/add"]')].filter(
        (form) => !this.contains(form)
      );
    }

    /** Walks up to the top-level block inside [data-product-blocks], if any. */
    toBlock(el) {
      const blocks = el.closest('[data-product-blocks]');
      if (!blocks) return el;
      let node = el;
      while (node.parentElement && node.parentElement !== blocks) node = node.parentElement;
      return node;
    }

    /* ------------------------------------------------------------------ */
    /* Units                                                              */
    /* ------------------------------------------------------------------ */

    /**
     * Writes a variant's price (and was-price) into the Bespoke price in the
     * head, formatted with the shop's money format through Motion's helper.
     * Without that helper the Liquid-rendered price is left as it is.
     * @param {{price: number, compare_at_price: ?number}} variant
     */
    updateBespokePrice(variant) {
      const formatMoney = window.theme && window.theme.Currency && window.theme.Currency.formatMoney;
      const moneyFormat = window.theme && window.theme.settings && window.theme.settings.moneyFormat;
      if (!variant || !this.bespokePriceEl || !formatMoney || !moneyFormat) return;

      const amount = document.createElement('span');
      amount.dataset.resorBespokeAmount = '';
      amount.textContent = formatMoney(variant.price, moneyFormat);

      const parts = [];
      if (variant.compare_at_price > variant.price) {
        const compare = document.createElement('s');
        compare.className = 'resor-switch__compare';
        compare.dataset.resorBespokeCompare = '';
        compare.textContent = formatMoney(variant.compare_at_price, moneyFormat);
        parts.push(compare);
      }
      parts.push(amount);
      this.bespokePriceEl.replaceChildren(...parts);
    }

    hasAvailableUnits() {
      return this.unitInputs.some((input) => !input.disabled);
    }

    /**
     * @param {HTMLInputElement} input selected unit radio
     * @param {{updateUrl?: boolean}} [options]
     */
    selectUnit(input, { updateUrl = true } = {}) {
      this.unitInputs.forEach((i) =>
        i.closest('[data-resor-unit]').classList.toggle('is-selected', i === input)
      );
      if (this.variantIdInput) this.variantIdInput.value = input.value;
      if (this.priceEl) this.priceEl.textContent = input.dataset.price;

      if (this.qtyInput) {
        const max = parseInt(input.dataset.max, 10) || 1;
        this.qtyInput.max = String(max);
        if (parseInt(this.qtyInput.value, 10) > max) this.qtyInput.value = String(max);
        // Only offer a quantity choice when more than one of this unit is on hand.
        const qtyRow = this.querySelector('[data-resor-qty-row]');
        if (qtyRow) qtyRow.hidden = max <= 1;
      }
      this.clearError();
      if (this.mode === 'stock') {
        this.loadPickup();
        if (updateUrl) this.syncUrl();
      }
    }

    /**
     * Re-checks availability of every listed unit when Stock is opened, so a
     * unit sold since the page loaded can't be picked. Throttled: switching
     * back and forth within AVAILABILITY_TTL reuses the last result. A failed
     * add to cart (422) always forces a fresh check.
     * @param {{force?: boolean}} [options]
     */
    async refreshAvailability({ force = false } = {}) {
      const now = Date.now();
      if (!force && this.lastAvailabilityCheck && now - this.lastAvailabilityCheck < AVAILABILITY_TTL) return;
      this.lastAvailabilityCheck = now;

      const availability = await this.fetchAvailability();
      this.unitInputs.forEach((input) => {
        if (availability.get(input.dataset.handle) === false) this.disableUnit(input);
      });
      this.afterAvailabilityChange();
    }

    /**
     * Availability per unit handle. One request for the whole stock collection
     * (the same list Liquid rendered from), so the check costs the same with
     * two units or twenty; any unit missing from that response (e.g. removed
     * from the collection) is checked on its own. Network errors leave a unit
     * as rendered: checkout still enforces stock.
     * @returns {Promise<Map<string, boolean>>}
     */
    async fetchAvailability() {
      const map = new Map();
      const headers = { Accept: 'application/json' };
      const collection = this.dataset.stockCollection;

      if (collection) {
        try {
          const res = await fetch(`${ROOT}collections/${collection}/products.json?limit=250`, { headers });
          if (res.ok) {
            const data = await res.json();
            (data.products || []).forEach((p) =>
              map.set(p.handle, (p.variants || []).some((v) => v.available))
            );
          }
        } catch (err) {
          /* fall through to the per-unit check */
        }
      }

      const missing = this.unitInputs.filter((input) => !map.has(input.dataset.handle));
      await Promise.allSettled(
        missing.map(async (input) => {
          try {
            const res = await fetch(`${ROOT}products/${input.dataset.handle}.js`, { headers });
            if (!res.ok) return;
            const data = await res.json();
            map.set(input.dataset.handle, Boolean(data.available));
          } catch (err) {
            /* keep as rendered */
          }
        })
      );
      return map;
    }

    /**
     * Greys out a unit that sold out after the page was rendered and swaps its
     * quantity text ("Last one" / "n available") for the sold-out text from
     * Theme settings → resør · Product page → In Stock panel.
     * @param {HTMLInputElement} input
     */
    disableUnit(input) {
      const unit = input.closest('[data-resor-unit]');
      input.disabled = true;
      input.checked = false;
      unit.classList.add('is-sold-out');
      unit.classList.remove('is-selected');

      const qty = unit.querySelector('[data-resor-unit-qty]');
      if (qty) {
        qty.textContent = this.dataset.soldOut || 'Sold out';
        qty.classList.remove('is-last');
      }
    }

    /**
     * Keeps a valid unit selected, or falls back to Bespoke (and shows the
     * "none in stock" text on the In Stock tile) when none are left.
     */
    afterAvailabilityChange() {
      this.updateStockCount();
      if (!this.hasAvailableUnits()) {
        this.disableStockMode();
        return;
      }
      const selected = this.unitInputs.find((i) => i.checked && !i.disabled);
      if (!selected) {
        const next = this.unitInputs.find((i) => !i.disabled);
        next.checked = true;
        this.selectUnit(next);
      }
    }

    /**
     * Re-renders the availability line ("4 in stock") from the units still
     * available, so the count matches the cards after a unit sells out.
     * Each unit counts its quantity on hand (data-max, rendered by Liquid).
     */
    updateStockCount() {
      if (!this.stockCountEl) return;
      const onHand = this.unitInputs
        .filter((input) => !input.disabled)
        .reduce((sum, input) => sum + (parseInt(input.dataset.max, 10) || 1), 0);
      if (onHand === 0) return; // stock mode is disabled instead; the line is never shown at 0
      const template = this.dataset.stockCountText || '[count] in stock';
      this.stockCountEl.textContent = template.replace(/\[count\]/g, String(onHand));
    }

    disableStockMode() {
      const stockInput = this.modeInputs.find((i) => i.value === 'stock');
      if (stockInput) stockInput.disabled = true;
      if (this.stockModeLabel) this.stockModeLabel.classList.add('is-disabled');
      if (this.stockNote) {
        if (this.dataset.noneInStock) this.stockNote.textContent = this.dataset.noneInStock;
        this.stockNote.hidden = false;
      }
      if (this.mode === 'stock') this.setMode('bespoke');
    }

    /* ------------------------------------------------------------------ */
    /* Pickup availability                                                */
    /* ------------------------------------------------------------------ */

    /**
     * Loads "Pickup available at …" for the selected unit into the stock panel,
     * from the same section Motion uses (/variants/<id>/?section_id=store-availability),
     * so the text and the store drawer are the theme's own and follow
     * Settings → Shipping and delivery → Local pickup for that unit's stock.
     * Skipped when the product template has pickup switched off (no theme
     * holder) or the unit is already loaded. An empty response (no pickup
     * location for the unit) or a network error leaves the area empty.
     */
    async loadPickup() {
      if (!this.pickupEl || !this.section.querySelector(PICKUP_HOLDER)) return;
      const unit = this.unitInputs.find((i) => i.checked && !i.disabled);
      if (!unit) {
        this.clearPickup();
        return;
      }
      const variantId = unit.value;
      if (this.pickupVariantId === variantId) return;
      this.pickupVariantId = variantId;

      let html = '';
      try {
        const res = await fetch(`${ROOT}variants/${variantId}/?section_id=store-availability`);
        if (res.ok) html = (await res.text()).trim();
      } catch (err) {
        /* leave empty: pickup is extra information, not part of buying */
      }
      // A different unit was picked while this one loaded: its own request wins.
      if (this.pickupVariantId !== variantId) return;
      if (!html) {
        this.clearPickup(); // also lets the next pick of this unit retry
        return;
      }

      // Same unwrapping as Motion: drop the outer shopify-section wrapper.
      const tpl = document.createElement('template');
      tpl.innerHTML = html;
      const wrapper = tpl.content.firstElementChild;
      this.pickupEl.innerHTML = wrapper ? wrapper.innerHTML : html;

      const drawer = this.pickupEl.querySelector(`#${PICKUP_DRAWER_ID}`);
      if (drawer) drawer.id = RESOR_DRAWER_ID;
      this.pickupEl.querySelectorAll(`.${PICKUP_OPENER_CLASS}`).forEach((link) => {
        link.classList.remove(PICKUP_OPENER_CLASS);
        link.classList.add(`js-drawer-open-${RESOR_DRAWER_NAME}`);
        link.setAttribute('href', `#${RESOR_DRAWER_ID}`);
        link.setAttribute('aria-controls', RESOR_DRAWER_ID);
      });
      // Drawer heading: the unit's name, as Motion shows the product name.
      const title = this.pickupEl.querySelector('[data-availability-product-title]');
      if (title) title.textContent = unit.dataset.title || '';

      // The drawer can't stay inside the switch: .resor-switch has a
      // view-transition-name (family strip cross-fade), which makes it its own
      // stacking layer, so the drawer would open underneath the page dimmer.
      // It goes where Motion's own drawer effectively sits: in #MainContent,
      // outside the switch. The previous unit's drawer is removed first.
      if (this.pickupDrawer) this.pickupDrawer.remove();
      this.pickupDrawer = drawer;
      if (drawer) {
        (document.getElementById('MainContent') || document.body).appendChild(drawer);
        this.whenThemeReady(() => new window.theme.Drawers(RESOR_DRAWER_ID, RESOR_DRAWER_NAME));
      }
    }

    /** Empties the pickup area and removes its store drawer. */
    clearPickup() {
      this.pickupEl.innerHTML = '';
      this.pickupVariantId = null;
      if (this.pickupDrawer) this.pickupDrawer.remove();
      this.pickupDrawer = null;
    }

    /**
     * Runs fn once Motion's theme.js (deferred) has defined theme.Drawers.
     * This file is async and can run first on a fast connection.
     * @param {Function} fn
     */
    whenThemeReady(fn) {
      const ready = () => window.theme && typeof window.theme.Drawers === 'function';
      if (ready()) {
        fn();
      } else if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => ready() && fn(), { once: true });
      }
    }

    /* ------------------------------------------------------------------ */
    /* Cart                                                               */
    /* ------------------------------------------------------------------ */

    /** Adds "Build: Bespoke · …" to the theme's own product form(s). */
    labelBespokeForm() {
      const value = this.dataset.bespokeProperty;
      if (!value) return;
      const name = `properties[${this.propertyKey}]`;

      this.getNativeForms().forEach((form) => {
        if (form.querySelector(`input[name="${name}"]`)) return;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      });
    }

    /** @param {SubmitEvent} event */
    async onSubmit(event) {
      event.preventDefault();
      this.clearError();
      this.setBusy(true);

      const formData = new FormData(this.stockForm);
      const quantity = Math.max(1, parseInt(formData.get('quantity'), 10) || 1);
      const item = {
        id: Number(formData.get('id')),
        quantity,
        properties: { [this.propertyKey]: formData.get(`properties[${this.propertyKey}]`) },
      };

      try {
        const res = await fetch(`${ROOT}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [item] }),
        });
        const data = await res.json();

        if (!res.ok) {
          // 422 = not enough stock (e.g. sold while on the page).
          this.showError(data.description || data.message || 'This unit could not be added.');
          this.refreshAvailability({ force: true });
          return;
        }

        this.afterAddToCart(data);
      } catch (err) {
        this.showError('Something went wrong. Please try again.');
      } finally {
        this.setBusy(false);
      }
    }

    /**
     * Mirrors Motion's own product form (theme.js → CartForm.success):
     * - Cart type "Page": go to the cart page.
     * - Cart type "Drawer": dispatch `ajaxProduct:added` on document. Motion's
     *   CartDrawer listens for it, rebuilds the cart (incl. the header count)
     *   and opens the drawer.
     * Cart type is read from Motion's runtime config (theme.settings.cartType),
     * falling back to the Liquid value; if no drawer exists, go to the cart page.
     *
     * @param {object} data response from /cart/add.js
     */
    afterAddToCart(data) {
      const runtime = window.theme && window.theme.settings && window.theme.settings.cartType;
      const cartType = runtime || this.dataset.cartType;
      const hasDrawer = Boolean(document.querySelector('#CartDrawer'));

      if (cartType === 'page' || !hasDrawer) {
        window.location.href = this.dataset.cartUrl || `${ROOT}cart`;
        return;
      }
      document.dispatchEvent(
        new CustomEvent('ajaxProduct:added', {
          detail: { product: data, addToCartBtn: this.submitBtn },
        })
      );
    }

    /* ------------------------------------------------------------------ */
    /* Helpers                                                            */
    /* ------------------------------------------------------------------ */

    syncUrl() {
      const url = new URL(window.location.href);
      if (this.mode === 'stock') {
        url.searchParams.set('build', 'stock');
        const selected = this.unitInputs.find((i) => i.checked);
        if (selected) url.searchParams.set('unit', selected.dataset.handle);
      } else {
        url.searchParams.delete('build');
        url.searchParams.delete('unit');
      }
      window.history.replaceState(window.history.state, '', url.toString());
    }

    setBusy(busy) {
      if (!this.submitBtn) return;
      this.submitBtn.disabled = busy;
      this.submitBtn.classList.toggle('btn--loading', busy);
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }
  }

  customElements.define('resor-order-switch', ResorOrderSwitch);
})();